import { Badge, Button, Select } from '@ui';
import { useCallback, useEffect, useState } from 'react';
import { bugApi, type LinkKind, type RepeatBadgeInfo, type RepeatFlagsView, type RepeatLink, type RepeatMode, type RepeatSuggestion, type RepeatView } from '../bugApi';
import { RepeatIcon } from '../icons';
import { formatDateTime } from '../labels';
import { emitRepeatDecided } from './repeatBus';

// Пов10 (вид — 👁): on a report's card — a note «Вероятно повтор №N · высокая · причины» with «Повтор», «Дополнение», «Другое», and the
// block «Похожие» (up to three, each with its reasons). The tracker only suggests (Пов1): a click is a person's decision (Пов9).

const KIND_WORDS: Record<LinkKind, string> = { repeat: 'повтор', continues: 'дополнение', not_helped: 'не помогло', other: 'другое' };

/** The words of a list badge: a confirmed link or a high suggestion. */
export function repeatBadgeText(repeat: RepeatBadgeInfo): string {
  if (repeat.kind === 'suggest') return `похоже на №${repeat.number}`;
  if (repeat.kind === 'repeat') return repeat.direction === 'earlier' ? `повтор №${repeat.number}` : `повторён в №${repeat.number}`;
  return repeat.direction === 'earlier' ? `дополняет №${repeat.number}` : `дополнен в №${repeat.number}`;
}

export function RepeatBadge({ repeat }: { repeat: RepeatBadgeInfo }) {
  return (
    <span data-bug-open="">
      <Badge tone={repeat.kind === 'suggest' ? 'warning' : 'info'}>{repeatBadgeText(repeat)}</Badge>
    </span>
  );
}

export const REPEAT_MODES: { value: RepeatMode; label: string }[] = [
  { value: '', label: 'Все репорты' },
  { value: 'repeat', label: 'Повторяется' },
  { value: 'back', label: 'Вернулось после исправления' },
  { value: 'unfinished', label: 'Недоделано' },
  { value: 'closed', label: 'Закрыто без подтверждения' },
  { value: 'stuck', label: 'Застряло' },
];

/** The one list of the filter beside the search (a chip would break Дк5): all reports, the repeating ones or one of the signs (Пов5–Пов8). */
export function RepeatFilter({ value, onChange }: { value: RepeatMode; onChange: (mode: RepeatMode) => void }) {
  return (
    <Select
      label="Повторы"
      hideLabel
      size="sm"
      value={value}
      onChange={(next) => onChange(next as RepeatMode)}
      options={REPEAT_MODES}
      className="board-epic"
    />
  );
}

/** Пов5–Пов8 (вид — 👁): the signs of a card, each one only shown. */
export function flagLabels(flags: RepeatFlagsView | undefined): { key: string; text: string; tone: 'warning' | 'error' | 'neutral' }[] {
  if (!flags) return [];
  const out: { key: string; text: string; tone: 'warning' | 'error' | 'neutral' }[] = [];
  if (flags.afterFix) {
    const hours = flags.afterFix.lagHours;
    out.push({
      key: 'back',
      text: `вернулось через ${Number.isInteger(hours) ? hours : String(hours).replace('.', ',')} ч после сборки ${flags.afterFix.build} (исправление №${flags.afterFix.number})`,
      tone: flags.afterFix.kind === 'soon' ? 'error' : 'warning',
    });
  }
  if (flags.unfinished) out.push({ key: 'unfinished', text: `недоделано (${flags.unfinished.level === 'likely' ? 'вероятно' : 'возможно'}): ${flags.unfinished.signs.join('; ')}`, tone: 'warning' });
  if (flags.closedNoProof) out.push({ key: 'closed', text: `закрыто без подтверждения: ${flags.closedNoProof}`, tone: 'neutral' });
  if (flags.stuckDays !== undefined) out.push({ key: 'stuck', text: `застряло ${flags.stuckDays} дн.`, tone: 'warning' });
  return out;
}

function Flags({ flags }: { flags: RepeatFlagsView | undefined }) {
  const labels = flagLabels(flags);
  if (labels.length === 0) return null;
  return (
    <div className="repeat-flags" data-testid="repeat-flags">
      {labels.map((label) => (
        <Badge key={label.key} tone={label.tone}>
          <span data-testid={`repeat-flag-${label.key}`}>{label.text}</span>
        </Badge>
      ))}
    </div>
  );
}

function Decide({ busy, onDecide }: { busy: boolean; onDecide: (kind: LinkKind) => void }) {
  return (
    <span className="repeat-actions">
      <Button size="sm" variant="tonal" disabled={busy} onClick={() => onDecide('repeat')}>
        Повтор
      </Button>
      <Button size="sm" variant="quiet" disabled={busy} onClick={() => onDecide('continues')}>
        Дополнение
      </Button>
      <Button size="sm" variant="quiet" disabled={busy} onClick={() => onDecide('other')}>
        Другое
      </Button>
    </span>
  );
}

function Reasons({ reasons }: { reasons: string[] }) {
  return (
    <ul className="repeat-reasons">
      {reasons.map((reason) => (
        <li key={reason} data-pii="">
          {reason}
        </li>
      ))}
    </ul>
  );
}

function Decided({ link, busy, onForget }: { link: RepeatLink; busy: boolean; onForget: () => void }) {
  return (
    <div className="repeat-decided" data-testid="repeat-decided">
      <Badge tone={link.kind === 'repeat' || link.kind === 'continues' ? 'success' : 'neutral'}>
        {KIND_WORDS[link.kind]} {link.direction === 'earlier' ? '→' : '←'} <span data-bug-open="">№{link.number}</span>
      </Badge>
      <span className="jui-muted" data-pii="">
        {link.decidedBy} · {formatDateTime(link.decidedAt)}
      </span>
      <Button size="sm" variant="quiet" disabled={busy} onClick={onForget}>
        Отменить
      </Button>
    </div>
  );
}

export function RepeatBlock({ reportId, onError }: { reportId: number; onError: (err: unknown, fallback: string) => void }) {
  const [view, setView] = useState<RepeatView | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setView(null);
    bugApi
      .similar(reportId)
      .then((res) => live && setView(res))
      .catch(() => live && setView(null));
    return () => {
      live = false;
    };
  }, [reportId]);

  const run = useCallback(
    async (job: () => Promise<RepeatView>, fallback: string) => {
      setBusy(true);
      try {
        const next = await job();
        setView(next);
        // Пов13: the open list or board shows the new badge at once.
        emitRepeatDecided(reportId, next);
      } catch (err) {
        onError(err, fallback);
      } finally {
        setBusy(false);
      }
    },
    [onError, reportId],
  );

  if (!view || (view.suggestions.length === 0 && view.links.length === 0 && flagLabels(view.flags).length === 0)) return null;
  const [top, ...rest] = view.suggestions;
  const banner: RepeatSuggestion | undefined = top?.confidence === 'high' ? top : undefined;
  const similar = banner ? rest : view.suggestions;
  const decide = (to: number) => (kind: LinkKind) => void run(() => bugApi.decide(reportId, to, kind), 'Не удалось записать решение');

  return (
    <div className="repeat-block" data-testid="repeat-block">
      <Flags flags={view.flags} />
      {banner && (
        <div className="repeat-banner" role="note" data-testid="repeat-banner">
          <div className="repeat-head">
            <RepeatIcon />
            <b>
              Вероятно повтор <span data-bug-open="">№{banner.number}</span>
            </b>
            <Badge tone="warning">высокая</Badge>
          </div>
          <Reasons reasons={banner.reasons} />
          <Decide busy={busy} onDecide={decide(banner.number)} />
        </div>
      )}
      {view.links.map((link) => (
        <Decided key={`${link.direction}-${link.id}`} link={link} busy={busy} onForget={() => void run(() => bugApi.forget(reportId, link.number), 'Не удалось отменить решение')} />
      ))}
      {similar.length > 0 && (
        <div className="repeat-similar" data-testid="repeat-similar">
          <div className="board-lane-title">
            Похожие <Badge>{similar.length}</Badge>
          </div>
          {similar.map((s) => (
            <div key={s.id} className="repeat-row">
              <div className="repeat-row-top">
                <span className="mono" data-bug-open="">
                  №{s.number}
                </span>
                <span className="repeat-excerpt" data-pii="">
                  {s.excerpt}
                </span>
              </div>
              <Reasons reasons={s.reasons} />
              <Decide busy={busy} onDecide={decide(s.number)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
